<template>
  <div class="gapup-library-wrap">
    <div class="gapup-library-toolbar">
      <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAdd">添加</el-button>
      <span v-if="rows.length" class="hint">共 {{ rows.length }} 条</span>
    </div>
    <div class="table-scroll">
      <el-table
        v-loading="loading"
        :data="rows"
        border
        size="mini"
        :max-height="maxHeight"
        class="gapup-library-table"
        empty-text="暂无补涨记录，点击添加"
      >
        <el-table-column label="龙头" min-width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.leaderName || row.leaderCode || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="name" label="股票名称" min-width="100" show-overflow-tooltip />
        <el-table-column prop="asOfDate" label="日期" min-width="100" sortable />
        <el-table-column
          prop="freeFloatMarketCap"
          label="流通市值(亿)"
          min-width="110"
          sortable
          :formatter="fmtNum"
        />
        <el-table-column prop="price" label="价格" min-width="80" sortable :formatter="fmtNum" />
        <el-table-column prop="ma250" label="MA250" min-width="90" sortable :formatter="fmtNum" />
        <el-table-column
          prop="distToYearHighPct"
          label="距年高涨幅"
          min-width="110"
          sortable
          :formatter="fmtDist"
        />
        <el-table-column
          prop="shortChip"
          label="短期筹码分"
          min-width="110"
          sortable
          :formatter="fmtChip"
        />
        <el-table-column
          prop="midChip"
          label="中期筹码分"
          min-width="110"
          sortable
          :formatter="fmtChip"
        />
        <el-table-column
          prop="longChip"
          label="长期筹码分"
          min-width="110"
          sortable
          :formatter="fmtChip"
        />
        <el-table-column
          prop="chipScoreSum"
          label="筹码总分"
          min-width="100"
          sortable
          :formatter="fmtChip"
        />
        <el-table-column
          prop="chipSlope"
          label="筹码分段斜率"
          min-width="120"
          sortable
          :formatter="fmtSlope"
        />
        <el-table-column
          prop="conceptScore"
          label="概念得分"
          min-width="90"
          sortable
          :formatter="fmtScore"
        />
        <el-table-column prop="concept" label="概念" min-width="140" show-overflow-tooltip />
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openEdit(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color: #f56c6c" @click="onRemove(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      :title="form.id ? '编辑补涨' : '添加补涨'"
      :visible.sync="formVisible"
      width="480px"
      append-to-body
      @closed="resetForm"
    >
      <el-form label-width="88px" size="small">
        <el-form-item label="日期" required>
          <el-date-picker
            v-model="form.asOfDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="计算筹码日期"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="补涨个股" required>
          <el-select
            v-model="form.code"
            filterable
            remote
            clearable
            :remote-method="searchGapStock"
            :loading="gapSearchLoading"
            placeholder="代码/名称搜索"
            style="width: 100%"
            @change="onGapStockChange"
          >
            <el-option
              v-for="opt in gapStockOptions"
              :key="'g-' + opt.code"
              :label="`${opt.name} (${opt.code})`"
              :value="opt.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="龙头" required>
          <el-select
            v-model="form.leaderCode"
            filterable
            remote
            clearable
            :remote-method="searchLeader"
            :loading="leaderSearchLoading"
            placeholder="代码/名称搜索"
            style="width: 100%"
            @change="onLeaderChange"
          >
            <el-option
              v-for="opt in leaderOptions"
              :key="'l-' + opt.code"
              :label="`${opt.name} (${opt.code})`"
              :value="opt.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="概念">
          <el-select
            v-model="form.concepts"
            multiple
            clearable
            filterable
            collapse-tags
            placeholder="可选，多选龙头概念"
            style="width: 100%"
            :loading="conceptLoading"
          >
            <el-option
              v-for="c in leaderConcepts"
              :key="c"
              :label="c"
              :value="c"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="formVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { stockApi } from '@/api/stockApi'
import { formatSlopeX1000Pct } from '@/utils/hfbChipFilters'

function emptyForm() {
  return {
    id: '',
    asOfDate: '',
    code: '',
    name: '',
    leaderCode: '',
    leaderName: '',
    concepts: []
  }
}

function normalizeCode(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6)
}

export default {
  name: 'GapUpRecordLibraryTable',
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    maxHeight: { type: [Number, String], default: 520 }
  },
  data() {
    return {
      formVisible: false,
      saving: false,
      form: emptyForm(),
      gapStockOptions: [],
      leaderOptions: [],
      gapSearchLoading: false,
      leaderSearchLoading: false,
      leaderConcepts: [],
      conceptLoading: false
    }
  },
  methods: {
    fmtNum(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-'
      const n = Number(cellValue)
      if (!Number.isFinite(n)) return '-'
      return n.toFixed(2)
    },
    fmtScore(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-'
      const n = Number(cellValue)
      if (!Number.isFinite(n)) return '-'
      return String(n)
    },
    fmtDist(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-'
      const n = Number(cellValue)
      if (!Number.isFinite(n)) return '-'
      return `${n.toFixed(2)}%`
    },
    fmtChip(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-'
      const n = Number(cellValue)
      if (!Number.isFinite(n)) return '-'
      return `${(n * 100).toFixed(2)}%`
    },
    fmtSlope(row, column, cellValue) {
      return formatSlopeX1000Pct(cellValue)
    },
    openAdd() {
      this.form = emptyForm()
      this.gapStockOptions = []
      this.leaderOptions = []
      this.leaderConcepts = []
      this.formVisible = true
    },
    openEdit(row) {
      const code = normalizeCode(row.code)
      const leaderCode = normalizeCode(row.leaderCode)
      const concepts = Array.isArray(row.concepts)
        ? row.concepts.filter(Boolean)
        : String(row.concept || '')
            .split(/[,，、]/)
            .map((x) => x.trim())
            .filter(Boolean)
      this.form = {
        id: row.id,
        asOfDate: row.asOfDate || '',
        code,
        name: row.name || '',
        leaderCode,
        leaderName: row.leaderName || '',
        concepts
      }
      this.gapStockOptions = code
        ? [{ code, name: row.name || code }]
        : []
      this.leaderOptions = leaderCode
        ? [{ code: leaderCode, name: row.leaderName || leaderCode }]
        : []
      this.formVisible = true
      if (leaderCode) this.loadLeaderConcepts(leaderCode)
    },
    resetForm() {
      this.form = emptyForm()
      this.saving = false
    },
    async searchGapStock(q) {
      const keyword = String(q || '').trim()
      if (!keyword) {
        this.gapStockOptions = []
        return
      }
      this.gapSearchLoading = true
      try {
        const response = await stockApi.searchStocks(keyword)
        const list = (response && response.success && response.data) || []
        this.gapStockOptions = (Array.isArray(list) ? list : []).map((r) => ({
          code: normalizeCode(r.code),
          name: r.name || ''
        }))
      } catch (e) {
        this.gapStockOptions = []
      } finally {
        this.gapSearchLoading = false
      }
    },
    async searchLeader(q) {
      const keyword = String(q || '').trim()
      if (!keyword) {
        this.leaderOptions = []
        return
      }
      this.leaderSearchLoading = true
      try {
        const response = await stockApi.searchStocks(keyword)
        const list = (response && response.success && response.data) || []
        this.leaderOptions = (Array.isArray(list) ? list : []).map((r) => ({
          code: normalizeCode(r.code),
          name: r.name || ''
        }))
      } catch (e) {
        this.leaderOptions = []
      } finally {
        this.leaderSearchLoading = false
      }
    },
    onGapStockChange(code) {
      const opt = this.gapStockOptions.find((o) => o.code === code)
      this.form.name = opt ? opt.name : ''
    },
    onLeaderChange(code) {
      const opt = this.leaderOptions.find((o) => o.code === code)
      this.form.leaderName = opt ? opt.name : ''
      this.form.concepts = []
      this.leaderConcepts = []
      if (code) this.loadLeaderConcepts(code)
    },
    async loadLeaderConcepts(code) {
      this.conceptLoading = true
      try {
        const body = await stockApi.fetchGapUpLeaderConcepts(code)
        this.leaderConcepts = Array.isArray(body && body.concepts) ? body.concepts : []
        if (body && body.name && !this.form.leaderName) this.form.leaderName = body.name
      } catch (e) {
        this.leaderConcepts = []
      } finally {
        this.conceptLoading = false
      }
    },
    async submitForm() {
      if (!this.form.asOfDate) {
        this.$message.warning('请选择日期')
        return
      }
      if (!this.form.code) {
        this.$message.warning('请选择补涨个股')
        return
      }
      if (!this.form.leaderCode) {
        this.$message.warning('请选择龙头')
        return
      }
      const payload = {
        asOfDate: this.form.asOfDate,
        code: this.form.code,
        name: this.form.name,
        leaderCode: this.form.leaderCode,
        leaderName: this.form.leaderName,
        concepts: Array.isArray(this.form.concepts) ? this.form.concepts : []
      }
      this.saving = true
      try {
        if (this.form.id) {
          const res = await stockApi.updateGapUpLibrary(this.form.id, payload)
          if (res && res.ok === false) {
            this.$message.error(res.message || '保存失败')
            return
          }
          this.$message.success('已更新')
        } else {
          const res = await stockApi.addGapUpLibrary(payload)
          if (res && res.ok === false) {
            this.$message.error(res.message || '保存失败')
            return
          }
          this.$message.success('已添加')
        }
        this.formVisible = false
        this.$emit('changed')
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '保存失败'
        this.$message.error(msg)
      } finally {
        this.saving = false
      }
    },
    onRemove(row) {
      this.$confirm(`确定删除 ${row.name || row.code}？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            const res = await stockApi.deleteGapUpLibrary(row.id)
            if (res && res.ok === false) {
              this.$message.error(res.message || '删除失败')
              return
            }
            this.$message.success('已删除')
            this.$emit('changed')
          } catch (e) {
            const msg =
              (e && e.response && e.response.data && e.response.data.message) ||
              (e && e.message) ||
              '删除失败'
            this.$message.error(msg)
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.gapup-library-wrap {
  min-height: 200px;
}
.gapup-library-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.gapup-library-toolbar .hint {
  color: #909399;
  font-size: 12px;
}
</style>
